import { ComboboxInput } from "@headlessui/react-v2";
import type { MutableRefObject, RefObject } from "react";
import { useCallback } from "react";

import { t } from "@/i18n-js/instance";

import type { ActiveOption } from "./types";

interface TopicInputProps {
  activeOption: ActiveOption;
  buttonRef: RefObject<HTMLButtonElement | null>;
  hasTopicsSelected?: boolean;
  isCreatingTopic: boolean;
  isDropdownOpen?: boolean;
  maxTopicsAllowed: number;
  onChange: (value: string) => void;
  onCreateTopic: (topicName: string) => void;
  onRemoveAllTopics: () => void;
  onRemoveLastTopic: () => void;
  placeholder?: string;
  searchInputRef: MutableRefObject<HTMLInputElement | null>;
  value: string;
}

export const TopicInput = ({
  value,
  onChange,
  activeOption,
  maxTopicsAllowed,
  isCreatingTopic,
  onCreateTopic,
  onRemoveLastTopic,
  onRemoveAllTopics,
  searchInputRef,
  buttonRef,
  placeholder,
  hasTopicsSelected,
  isDropdownOpen,
}: TopicInputProps) => {
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const { key, ctrlKey: isCtrlPressed, metaKey: isMetaPressed } = event;

    if ((isCtrlPressed || isMetaPressed) && key === "Backspace") {
      onRemoveAllTopics();
    } else if (key === "Backspace" && value.length === 0) {
      onRemoveLastTopic();
    } else if (
      key === "Enter" &&
      activeOption === "create" &&
      !isCreatingTopic &&
      value.trim().length > 0
    ) {
      event.preventDefault();
      onCreateTopic(value);
    }
  };

  const placeholderText = hasTopicsSelected
    ? ""
    : placeholder ||
      t("quick_post_v2.topics.search", {
        count: maxTopicsAllowed,
      });

  const handleOpenDropdown = useCallback(() => {
    if (isDropdownOpen) return;
    buttonRef.current?.click();
  }, [isDropdownOpen]);

  return (
    <ComboboxInput
      className="min-w-[200px] flex-1 border-none bg-transparent p-0 px-1 py-[5px] text-sm focus:ring-0"
      ref={searchInputRef}
      value={value}
      placeholder={placeholderText}
      onChange={event => onChange(event.target.value)}
      onFocus={handleOpenDropdown}
      onClick={handleOpenDropdown}
      onKeyDown={handleKeyDown}
    />
  );
};
